import { Box, Button, Tooltip } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useMenuAnchor } from '~/hooks/useMenuAnchor';
import {
    CONSOLE_CHEVRON_CLASS,
    CONSOLE_DISABLED_SX,
    CONSOLE_CHEVRON_SX,
    CONSOLE_HOVER_SURFACE_SX,
    CONSOLE_TEXT_BUTTON_SX,
    TRIGGER_CHEVRON_SIZE_PX,
} from '../chrome';
import ConsoleMenu from '../ConsoleMenu';
import { ConsoleChoice, consoleChoiceItems } from './consoleChoiceItems';

interface ConsoleChoiceMenuProps<T> {
    /** What the list chooses, for assistive technology and the test id. */
    readonly name: string;
    readonly testId: string;
    readonly choices: ReadonlyArray<ConsoleChoice<T>>;
    readonly value: T;
    readonly onSelect: (value: T) => void;
    /**
     * How the trigger sits in the bar. `swatch`: an icon alone at the
     * pickers' height, for a choice whose icon says enough; the name and
     * the value reach assistive technology through the trigger's label and
     * the pointer through its tooltip. `picker`: a framed picker like the
     * bar's image pickers, the value led by an icon.
     */
    readonly appearance: 'swatch' | 'picker';
    /**
     * The icon the trigger shows; without it, the icon of the choice in
     * force.
     */
    readonly glyph?: ReactNode;
    /** Which edge of the trigger the list grows from. */
    readonly menuAlign?: 'left' | 'right';
    /** How the value reads when no choice holds it. */
    readonly fallbackLabel?: string;
    /** Refuses to open the list. */
    readonly disabled?: boolean;
}

/**
 * A value in force and the fixed list that changes it, in the footer;
 * the list marks the entry in force, and choosing it again changes
 * nothing.
 */
export default function ConsoleChoiceMenu<T>({
    name,
    testId,
    choices,
    value,
    onSelect,
    appearance,
    glyph,
    menuAlign = 'left',
    fallbackLabel,
    disabled = false,
}: ConsoleChoiceMenuProps<T>) {
    const menu = useMenuAnchor();
    const current = choices.find((choice) => choice.value === value);
    const label = current?.label ?? fallbackLabel;
    const ariaLabel = label ? `${name}: ${label}` : name;
    const onOpen = (event: ReactMouseEvent<HTMLButtonElement>) =>
        menu.onOpen(event.currentTarget);

    return (
        <>
            {appearance === 'picker' ? (
                <Button
                    variant="picker"
                    aria-label={ariaLabel}
                    aria-haspopup="menu"
                    aria-expanded={menu.open}
                    data-testid={testId}
                    disabled={disabled}
                    onClick={onOpen}
                    startIcon={glyph ?? current?.icon}
                    endIcon={<ChevronDown size={TRIGGER_CHEVRON_SIZE_PX} />}
                >
                    {label}
                </Button>
            ) : (
                // A disabled button fires no events, so it carries no
                // tooltip; always wrapped, so a change of state never
                // remounts the button and drops its focus.
                <Tooltip title={disabled ? '' : ariaLabel} placement="top">
                    <Box
                        component="button"
                        type="button"
                        aria-label={ariaLabel}
                        aria-haspopup="menu"
                        aria-expanded={menu.open}
                        data-testid={testId}
                        disabled={disabled}
                        onClick={onOpen}
                        sx={[
                            CONSOLE_TEXT_BUTTON_SX,
                            CONSOLE_HOVER_SURFACE_SX,
                            CONSOLE_DISABLED_SX,
                        ]}
                    >
                        {glyph ?? current?.icon}
                        <Box
                            component="span"
                            aria-hidden
                            className={CONSOLE_CHEVRON_CLASS}
                            sx={CONSOLE_CHEVRON_SX}
                        >
                            <ChevronDown size={11} />
                        </Box>
                    </Box>
                </Tooltip>
            )}
            <ConsoleMenu
                anchorEl={menu.anchorEl}
                open={menu.open}
                onClose={menu.onClose}
                minWidth={200}
                align={menuAlign}
            >
                {consoleChoiceItems({
                    choices,
                    value,
                    onSelect,
                    onDone: menu.onClose,
                })}
            </ConsoleMenu>
        </>
    );
}
